<template>
  <div class="mb-n3 mb-md-n5" :class="{ 'h-100': presentationStore.forceFullSizeSlide }">
    <slot name="before">
      <!--content before sticky goes here (sticky will be stuck to bottom of viewport) -->
    </slot>
    <div
      v-show="stickies"
      class="my-sticky-bottom mt-3 mt-md-5 mx-n3 mx-md-n5 sticky-shadow z-2"
      :class="{ 'shadow-sm': !isSticky }"
    >
      <button
        v-if="presentationStore.displayScrollToBottom"
        class="btn btn-light rounded-0 border-top w-100 button-override"
        @click="scrollToBottom"
      >
        <AVIcon icon="chevron-down" />
        {{ $t("js.components.AVSubmissionHelper.go_to_bottom") }}
      </button>
      <template v-for="sticky in stickies">
        <BallotStatus
          v-if="sticky.type === 'ballotStatus'"
          :voted="sticky.voted"
          :can-vote="sticky.canVote"
          :ballot-slide="sticky.ballot"
        />
        <ContestStatus
          v-else-if="sticky.type === 'contestStatus'"
          :contest="sticky.contest"
          :active-selection-pile="sticky.activeSelectionPile"
          :includeLazyErrors="sticky.includeLazyErrors"
        />
        <div v-else class="text-sm-center px-3 py-2 bg-theme" :class="sticky.class">
          <span class="sub-lead">{{ sticky.message }}</span>
        </div>
      </template>
    </div>
    <a id="sticky-anchor" ref="stickyAnchor"></a>
    <slot name="after">
      <!-- content after sticky goes here (sticky will scroll out of view with the content in "before" slot -->
    </slot>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, provide } from "vue";
import BallotStatus from "./sticky_alerts/BallotStatus.vue";
import ContestStatus from "./sticky_alerts/ContestStatus.vue";
import { usePresentationStore } from "@/entrypoints/stores/presentation";
import type { Subscriber, Sticky } from "@/types";

const presentationStore = usePresentationStore();

const stickyAnchor = ref<HTMLElement | null>(null);

const subscribers = ref<Array<Subscriber>>([]);

const isSticky = ref<boolean>(false);

const stickies = computed(() => {
  const alerts = [];
  subscribers.value.forEach((subscriber: Subscriber) =>
    subscriber.stickies.forEach((alert: Sticky) => alerts.push(alert)));
  return alerts;
});

const stickyAlertsSubscribe = (component: Subscriber) => subscribers.value.push(component);

const stickyAlertsUnsubscribe = (component: Subscriber) => {
  const subscriberIndex = subscribers.value.findIndex((subscriber: Subscriber) => subscriber === component);
  subscribers.value.splice(subscriberIndex, 1);
};

const scrollToBottom = () => document.querySelector("#ballot-action-buttons")?.scrollIntoView({ behavior: "smooth", block: "start" });

onMounted(() => {
  const options = {
    root: null,
    rootMargin: "0px",
    threshold: 0.0,
  }
  new IntersectionObserver((entries, _observer) => {
    isSticky.value = !entries[0].isIntersecting;
  }, options).observe(stickyAnchor.value!);
});

provide("stickyAlertsSubscribe", stickyAlertsSubscribe);

provide("stickyAlertsUnsubscribe", stickyAlertsUnsubscribe);
</script>

<style scoped>
.button-override {
  background-color: var(--bs-gray-300);

  &:hover {
    background-color: var(--bs-gray-500);
  }
}
</style>
